import React, { useState, useEffect, useCallback } from 'react'
import { Button, Input, Dropdown } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import { Icon } from '@/icons';
import { vars } from 'style/theme.css'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import Pagination from 'components/atoms/Pagination/Pagination';
import { academyTrophyBadgeApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';

interface TrophyBadgeAward {
  id: number
  trophyBadgeId: number
  trophyBadgeName: string
  iconUrl?: string
  type: 'trophy' | 'badge'
  awardType?: 'auto' | 'manual' | string
  studentUserId: number
  studentName: string
  studentLoginId?: string
  awardedAt: string
  grantedByName?: string
}

export default function AcademyTrophyBadgeList() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const slugPrefix = getSlugPrefix(pathname);
  const initialType = searchParams.get('type') || 'all';
  const initialPage = Number(searchParams.get('page')) || 1;
  const [loading, setLoading] = useState(false);
  const [awards, setAwards] = useState<TrophyBadgeAward[]>([]);
  const [searchType, setSearchType] = useState<string>(initialType);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 학원 코드 가져오기
  const { academyCode: sessionAcademyCode, status: sessionStatus } = useSessionSlug();
  const academyCode = sessionAcademyCode || '';

  useEffect(() => {
    if (sessionStatus !== 'ready' || !academyCode) return;
    fetchAwards();
  }, [searchType, academyCode, sessionStatus]);

  const syncSearchParams = useCallback((nextType: string, nextPage: number) => {
    const params = new URLSearchParams();
    if (nextType && nextType !== 'all') params.set('type', nextType);
    if (nextPage > 1) params.set('page', String(nextPage));
    setSearchParams(params);
  }, [setSearchParams]);

  useEffect(() => {
    syncSearchParams(searchType, currentPage);
  }, [searchType, currentPage, syncSearchParams]);

  const fetchAwards = async () => {
    if (!academyCode) {
      console.error('학원 코드가 없습니다.');
      return;
    }

    setLoading(true);
    try {
      const response = await academyTrophyBadgeApi.listAwards({
        academyCode,
        type: searchType !== 'all' ? searchType : undefined,
      });
      const data = response?.data || response;
      const content = data?.content || [];
      setAwards(content);
      setTotal(content.length);
    } catch (error) {
      console.error('트로피/뱃지 수여 목록 조회 실패:', error);
      setAwards([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (awardId: number) => {
    navigate(
      `${slugPrefix}/academy/student/trophy_badge/detail/${awardId}`,
      { state: { from: `${pathname}${search}` } },
    );
  };

  const handleAddClick = () => {
    navigate(
      `${slugPrefix}/academy/student/trophy_badge/detail/new`,
      { state: { from: `${pathname}${search}` } },
    );
  };

  // 페이지네이션을 위한 데이터 슬라이싱
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAwards = awards.slice(startIndex, endIndex);

  const typeOptions = [
    { label: '전체', value: 'all' },
    { label: '트로피', value: 'trophy' },
    { label: '뱃지', value: 'badge' },
  ];

  // 컬럼 렌더링용 메타 정보
  const columns = [
    { 
      key: 'no' as const, 
      render: (_: TrophyBadgeAward, index: number) => total - (startIndex + index)
    },
    { 
      key: 'type' as const, 
      render: (row: TrophyBadgeAward) => (
        <span className="font-semibold">
          {row.type === 'trophy' ? '트로피' : '뱃지'} - {row.trophyBadgeName}
        </span>
      )
    },
    { 
      key: 'recipient' as const, 
      render: (row: TrophyBadgeAward) => (
        <div className="flex gap-2 items-center lg:block">
          <span>
            {row.studentName}
            {row.studentLoginId ? <span className='text-gray-500 text-sm'> ({row.studentLoginId})</span> : null /* [QA Fix] 수여대상자 ID 노출 */}
          </span>
        </div>
      )
    },
    { 
      key: 'giver' as const, 
      render: (row: TrophyBadgeAward) => (
        <div className="flex gap-2 items-center lg:block">
          <span>{row.awardType === 'auto' ? '자동' : (row.grantedByName || '-')}</span>
        </div>
      )
    },
    { 
      key: 'awardDate' as const, 
      render: (row: TrophyBadgeAward) => (
        <div className="flex gap-2 items-center lg:block">
          <span>{String(row.awardedAt).slice(0, 10)}</span>
        </div>
      )
    },
  ];

  return (
    <main className='flex flex-col flex-1'>
      <div className='flex flex-row justify-between items-center pt-8'>
        <h1 className='heading1'>트로피 및 뱃지 관리</h1>
      </div>
      <section className='flex flex-wrap gap-2 justify-between py-2 pt-3 w-full'>
        <div className='flex flex-[600px] gap-2'>
          <Dropdown 
            placeholder='유형 검색' 
            className='max-w-[150px] w-full'
            options={typeOptions}
            value={searchType}
            onChange={(value) => {
              setSearchType(value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className='flex gap-2 justify-end'>
          <Button onClick={handleAddClick}>트로피 및 뱃지 수여하기</Button>
        </div>
      </section>
      <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
        {loading ? (
          <div className="text-center py-10">
            <p>로딩 중...</p>
          </div>
        ) : paginatedAwards.length === 0 ? (
          <div className="text-center py-10">
            <p>리스트가 없습니다.</p>
          </div>
        ) : (
          <div className='w-full'>
            {/* 테이블 헤더 pc 용*/}
            <ul className={`${styles.academyHeaderList} w-full`}>
              <li className={`${styles.headerItem} max-w-[60px] justify-center`}>순번</li>
              <li className={`${styles.headerItem} w-full`}>트로피 뱃지</li>
              <li className={`${styles.headerItem} w-full`}>수여 대상자</li>
              <li className={`${styles.headerItem} w-full`}>수여자</li>
              <li className={`${styles.headerItem} w-full`}>수여일</li>
            </ul>
            <div className='flex flex-col w-full lg:gap-0'>
              {paginatedAwards.map((row, index) => (
                <ul 
                  key={row.id} 
                  className={`${styles.academyBodyList} cursor-pointer hover:bg-gray-50 transition-colors`}
                  onClick={() => handleRowClick(row.id)}
                >
                  {columns.map(({ key, render }) => (
                    <li
                      key={key}
                      className={`${styles.bodyItem} ${
                        key === 'no' ? 'max-w-[60px] justify-center' :
                        key === 'type' ? 'font-bold w-full' :
                        key === 'recipient' ? 'text-sm text-gray-500 w-full' :
                        key === 'giver' ? 'text-sm text-gray-500 w-full' :
                        key === 'awardDate' ? 'text-sm text-gray-500 w-full lg:static col-span-1' :
                        ''
                      }`}
                    >
                      {render(row, index)}
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        )}
        {total > 0 && (
          <div className='py-5'>
            <Pagination
              total={total}
              pageSize={pageSize}
              current={currentPage}
              onChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </section>
    </main>
  )
}
